import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Steps,
  Tag,
  Typography,
  notification,
} from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import routes from "../../../engine/routes";
import { getInstitutions } from "../Slices/Institutions.slice";
import {
  MERGE_PRESET_GROUPS,
  MERGE_STATUS,
} from "./institutionMerge.constants";
import { mergeApi, errMsg } from "./institutionMerge.api";
import { StepConfig } from "./steps/StepConfig";
import { StepUsers } from "./steps/StepUsers";
import { StepBlacklists } from "./steps/StepBlacklists";
import { StepPreview } from "./steps/StepPreview";
import { StepAddresses } from "./steps/StepAddresses";
import { StepResponsables } from "./steps/StepResponsables";
import { StepResult } from "./steps/StepResult";
import { MergeSummary } from "./steps/MergeSummary";
import { StepGuide } from "./steps/StepGuide";
import { DECISION_STEPS, pendingOf } from "./steps/pending";
import "./merge.scss";

const { Title, Text, Paragraph } = Typography;

const STEPS = [
  { key: "sources", title: "Établissements" },
  { key: "config", title: "Configuration" },
  { key: "users", title: "Comptes" },
  { key: "addresses", title: "Adresses" },
  { key: "responsables", title: "Responsables" },
  { key: "blacklists", title: "Listes noires" },
  { key: "preview", title: "Enregistrements" },
  { key: "result", title: "Avant / Après" },
  { key: "apply", title: "Appliquer" },
];
// step indexes — every dependency gets its own decision step, then the records, then the exact before/after
const S = {
  sources: 0,
  config: 1,
  users: 2,
  addresses: 3,
  responsables: 4,
  blacklists: 5,
  preview: 6,
  result: 7,
  apply: 8,
};

/**
 * Fusion d'institutions — the wizard.
 * Step 1 creates the merge record (a stateless preview first, so the operator sees the impact before anything is stored).
 * Steps 2–4 are the decisions; apply stays disabled until the server says every one is answered.
 */
export function InstitutionMerge() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { institutions } = useSelector(state => state.institutions);

  const [step, setStep] = useState(0);
  const [sourceIds, setSourceIds] = useState([]);
  const [donorId, setDonorId] = useState(null);
  const [label, setLabel] = useState("");
  // the new institution's name is asked right on step 1 and carried into the configuration step (decisions.targetName)
  const [newName, setNewName] = useState("");
  // "new": the merge creates the institution. "existing": sources join an institution that already exists
  // (e.g. a previous merge's target when more sites come in later) — no second institution is created.
  const [targetMode, setTargetMode] = useState("new");
  const [existingTargetId, setExistingTargetId] = useState(null);
  const [merge, setMerge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const saveQueue = useRef(Promise.resolve());
  const saveSeq = useRef(0);

  // two institutions with the same name would be indistinguishable in a dropdown — and merging the wrong one
  // is exactly the mistake this tool must not invite. Homonyms get their Abacus id or id tail appended.
  const options = useMemo(() => {
    const live = (institutions || []).filter(i => !i.archived);
    const byName = new Map();
    for (const i of live) byName.set(i.name, (byName.get(i.name) || 0) + 1);
    return live.map(i => ({
      value: i._id,
      label:
        byName.get(i.name) > 1
          ? `${i.name} (${
              i.abacusId
                ? `Abacus ${i.abacusId}`
                : `…${String(i._id).slice(-6)}`
            })`
          : i.name,
    }));
  }, [institutions]);
  const disabled = !!merge && merge.status !== "previewed";
  const preview = merge?.preview;
  const decisions = merge?.decisions || {};

  // the merge id already loaded (or just created): the URL effect must not GET it a second time
  const loadedId = useRef(null);
  const loadMerge = useCallback(async id => {
    try {
      const m = await mergeApi.get(id);
      setMerge(m);
      setSourceIds(m.sourceInstitutionIds.map(s => s._id || s));
      setDonorId(m.configDonorId?._id || m.configDonorId);
      setLabel(m.label);
      setTargetMode(m.targetMode || "new");
      setExistingTargetId(
        m.targetMode === "existing"
          ? m.targetInstitutionId?._id || m.targetInstitutionId
          : null,
      );
      // opening a merge from its URL leaves « Établissements » for the first decision step — never
      // moves an operator who is already further on
      setStep(s => (s === 0 ? 1 : s));
    } catch (e) {
      loadedId.current = null;
      notification.error({
        message: "Fusion introuvable",
        description: errMsg(e),
      });
    }
  }, []);

  useEffect(() => {
    dispatch(getInstitutions(""));
  }, [dispatch]);
  const urlId = params.get("id");
  useEffect(() => {
    if (!urlId || urlId === loadedId.current) return;
    loadedId.current = urlId;
    loadMerge(urlId);
  }, [urlId, loadMerge]);

  const applyPreset = key => {
    const g = MERGE_PRESET_GROUPS.find(p => p.key === key);
    if (!g) return;
    setSourceIds(g.sourceIds);
    setDonorId(g.configDonorId);
    setLabel(g.label);
    setNewName(g.targetName || "");
  };

  const existing = targetMode === "existing";
  const targetName = existing
    ? preview?.existingTarget?.name ||
      options.find(o => o.value === existingTargetId)?.label ||
      ""
    : decisions.targetName;
  const create = async () => {
    if (sourceIds.length < (existing ? 1 : 2))
      return notification.warning({
        message: existing
          ? "Sélectionnez au moins un établissement à fusionner"
          : "Sélectionnez au moins deux établissements",
      });
    if (existing && !existingTargetId)
      return notification.warning({
        message: "Choisissez l'institution existante qui reçoit la fusion",
      });
    if (!existing && !newName.trim())
      return notification.warning({
        message: "Donnez un nom à la nouvelle institution",
      });
    setBusy(true);
    try {
      const autoLabel = existing
        ? `${sourceIds.length} établissement(s) → ${targetName}`
        : `${newName.trim()} (${sourceIds.length} établissements)`;
      const m = await mergeApi.create(
        sourceIds,
        donorId || (existing ? existingTargetId : sourceIds[0]),
        label.trim() || autoLabel,
        existing ? existingTargetId : undefined,
        existing ? undefined : newName.trim(),
      );
      const full = await mergeApi.get(m._id);
      setMerge(full);
      loadedId.current = String(m._id);
      setParams({ id: m._id });
      setStep(1);
      notification.success({
        message: "Prévisualisation prête",
        description: `${full.preview.totals.moves} enregistrements concernés`,
      });
    } catch (e) {
      notification.error({ message: "Erreur", description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const saveDecisions = async patch => {
    // optimistic: merge the patch locally, then persist
    setMerge(m => ({
      ...m,
      decisions: {
        ...m.decisions,
        ...Object.fromEntries(
          Object.entries(patch).map(([k, v]) => [
            k,
            v && typeof v === "object" && !Array.isArray(v)
              ? { ...(m.decisions[k] || {}), ...v }
              : v,
          ]),
        ),
      },
    }));
    // persist in order: PUTs are queued so a slow earlier save can never overwrite a later one,
    // and the readiness refresh only lands when no newer save is still pending
    const seq = ++saveSeq.current;
    saveQueue.current = saveQueue.current
      .then(() => mergeApi.decisions(merge._id, patch))
      .then(async () => {
        if (seq !== saveSeq.current) return; // a newer decision is on its way
        const fresh = await mergeApi.get(merge._id);
        if (seq === saveSeq.current) setMerge(fresh);
      })
      .catch(async e => {
        notification.error({
          message: "Décision non enregistrée",
          description: errMsg(e),
          duration: 0,
        });
        // the optimistic state showed a decision the server refused: show what is really stored
        try {
          const fresh = await mergeApi.get(merge._id);
          if (seq === saveSeq.current) setMerge(fresh);
        } catch (_) {
          /* the error above already tells the operator */
        }
      });
    return saveQueue.current;
  };

  const discard = () =>
    Modal.confirm({
      title: "Abandonner cette fusion ?",
      content:
        "Rien n'a été écrit dans la base : la prévisualisation et les décisions sont simplement supprimées.",
      okType: "danger",
      okText: "Abandonner",
      onOk: async () => {
        setBusy(true);
        try {
          await mergeApi.discard(merge._id);
          notification.success({ message: "Fusion abandonnée" });
          setMerge(null);
          loadedId.current = null;
          setParams({});
          setStep(0);
        } catch (e) {
          notification.error({ message: "Erreur", description: errMsg(e) });
        } finally {
          setBusy(false);
        }
      },
    });

  const refresh = async () => {
    setBusy(true);
    try {
      // labels come from the preview the decisions were taken on (the fresh one no longer has the dropped items)
      const before = merge.preview || {};
      const r = await mergeApi.refresh(merge._id);
      setMerge(await mergeApi.get(merge._id));
      notification.success({ message: "Prévisualisation rafraîchie" });
      // decisions on items that no longer exist are removed by the server — say so, never silently.
      // Server shape: { users: [e-mail], blacklists: [interpreterId], addresses: [id], responsables: [id] };
      // an array or a number is still understood
      const raw = r && r.droppedDecisions;
      const DROPPED_KINDS = {
        addresses: "adresse(s)",
        users: "compte(s) en double",
        responsables: "responsable(s)",
        blacklists: "exclusion(s) d'interprète",
      };
      const labelOf = (kind, x) => {
        if (x && typeof x === "object")
          return x.label || x.name || x.email || x.id || x.key || "?";
        const id = String(x);
        if (kind === "addresses") {
          const a = (before.addresses?.items || []).find(i => i._id === id);
          return a
            ? [a.name, [a.street, a.number].filter(Boolean).join(" "), a.city]
                .filter(Boolean)
                .join(", ")
            : id;
        }
        if (kind === "responsables") {
          const o = (before.responsables?.items || []).find(i => i._id === id);
          return o
            ? [o.firstname, o.lastname].filter(Boolean).join(" ") ||
                o.email ||
                id
            : id;
        }
        if (kind === "blacklists") {
          const b = (before.blacklists || []).find(i => i.interpreterId === id);
          return b ? b.name || b.interpreterName || b.email || id : id;
        }
        return id;
      };
      let groups = [];
      if (Array.isArray(raw)) groups = [{ kind: null, items: raw }];
      else if (raw && typeof raw === "object")
        groups = Object.entries(raw)
          .filter(([, xs]) => Array.isArray(xs) && xs.length)
          .map(([kind, items]) => ({ kind, items }));
      const n =
        groups.reduce((t, g) => t + g.items.length, 0) || Number(raw) || 0;
      if (n) {
        const summary = groups
          .filter(g => g.kind)
          .map(g => `${g.items.length} ${DROPPED_KINDS[g.kind] || g.kind}`)
          .join(", ");
        notification.warning({
          message: `${n} décision(s) retirée(s) : les éléments n'existent plus`,
          description: groups.length ? (
            <div>
              {summary && <div style={{ marginBottom: 4 }}>{summary}</div>}
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {groups.flatMap(g =>
                  g.items.slice(0, 20).map((x, i) => (
                    <li key={`${g.kind}:${i}`}>
                      {g.kind
                        ? `${(DROPPED_KINDS[g.kind] || g.kind).replace(
                            "(s)",
                            "",
                          )} : `
                        : ""}
                      {labelOf(g.kind, x)}
                    </li>
                  )),
                )}
                {groups.some(g => g.items.length > 20) && <li>…</li>}
              </ul>
            </div>
          ) : (
            undefined
          ),
          duration: 0,
        });
      }
    } catch (e) {
      notification.error({ message: "Erreur", description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const v = await mergeApi.apply(merge._id);
      notification[v.ok ? "success" : "warning"]({
        message: v.ok
          ? "Fusion appliquée et vérifiée"
          : "Fusion appliquée — vérification incomplète",
        description: v.ok
          ? `Reste sur les sources : ${v.residualTotal}`
          : `${v.missing.length} enregistrement(s) manquant(s), ${v.residualTotal} restant(s) sur les sources`,
        duration: 8,
      });
      navigate(routes.institutions().mergeDetail(merge._id).path);
    } catch (e) {
      notification.error({
        message: "Fusion refusée",
        description: (
          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{errMsg(e)}</pre>
        ),
        duration: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  const readiness = merge?.readiness || { blockers: [], ready: false };
  // what is still to decide per step — one helper (steps/pending.js) feeds the badges, the
  // footer counter and each step's guide, so the three never disagree
  const pendingFor = k => pendingOf(k, preview, decisions, { readiness });
  const stepBadge = k => {
    if (!preview || !DECISION_STEPS.includes(k)) return null;
    const n = pendingFor(k).length;
    return n ? <Tag color="red">{n}</Tag> : <Tag color="green">ok</Tag>;
  };
  // step « Établissements » has no preview yet: its pending items come from the form itself
  const sourcesPending = merge
    ? []
    : [
        ...(sourceIds.length < (existing ? 1 : 2)
          ? [
              {
                key: "sources",
                label: existing
                  ? "au moins un établissement à fusionner"
                  : "au moins deux établissements",
              },
            ]
          : []),
        ...(existing && !existingTargetId
          ? [{ key: "target", label: "l'institution existante qui reçoit" }]
          : []),
        ...(!existing && !newName.trim()
          ? [{ key: "name", label: "le nom de la nouvelle institution" }]
          : []),
      ];
  const stepPending = merge && step > 0 ? pendingFor(STEPS[step].key) : [];
  const guide = k => (
    <StepGuide
      stepKey={k}
      preview={preview}
      decisions={decisions}
      readiness={readiness}
      pending={k === "sources" ? sourcesPending : undefined}
    />
  );

  return (
    <div className="merge-page" style={{ padding: "24px 24px 96px" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
      >
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>
            Fusion d'institutions
          </Title>
          {merge && (
            <Space>
              <Text type="secondary">{merge.label}</Text>
              <Tag color={MERGE_STATUS[merge.status]?.color}>
                {MERGE_STATUS[merge.status]?.label || merge.status}
              </Tag>
            </Space>
          )}
        </div>
        <Space>
          <Link to={routes.institutions().mergeHistory.path}>
            <Button>Historique des fusions</Button>
          </Link>
          {merge && (
            <Link to={routes.institutions().mergeDetail(merge._id).path}>
              <Button>Détail</Button>
            </Link>
          )}
        </Space>
      </Space>

      {/* nine steps: small + label under the icon, the badge as description — titles never collide at 1280 / 1440 px */}
      <Steps
        className="merge-steps"
        size="small"
        labelPlacement="vertical"
        style={{ margin: "24px 0" }}
        current={step}
        onChange={i => merge && setStep(i)}
        items={STEPS.map(s => ({
          title: s.title,
          description: stepBadge(s.key),
          disabled: !merge && s.key !== "sources",
        }))}
      />

      {step === 0 && (
        <Card title="1. Établissements à fusionner">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {guide("sources")}
            <Alert
              type="info"
              showIcon
              message="Tous les établissements sélectionnés sont des sources : leurs données sont déplacées vers l'institution résultante (nouvelle, ou existante si vous le choisissez ci-dessous) et ils sont archivés. L'établissement de référence ne sert qu'à pré-remplir la configuration."
            />
            <Space wrap>
              <Text>Préréglage</Text>
              <Select
                disabled={!!merge}
                style={{ width: 360 }}
                placeholder="Choisir un groupe…"
                options={MERGE_PRESET_GROUPS.map(g => ({
                  value: g.key,
                  label: g.label,
                }))}
                onChange={applyPreset}
                allowClear
              />
              <Text type="secondary">
                raccourci pour les groupes demandés par le client — n'importe
                quelle institution peut être sélectionnée ci-dessous, seule ou
                avec d'autres
              </Text>
            </Space>
            <div>
              <Text strong>Établissements (sources)</Text>
              <Select
                disabled={!!merge}
                mode="multiple"
                style={{ width: "100%", marginTop: 6 }}
                options={options}
                value={sourceIds}
                onChange={setSourceIds}
                optionFilterProp="label"
                placeholder="Sélectionner…"
              />
            </div>
            <div>
              <Text strong>Résultat</Text>
              <div style={{ marginTop: 6 }}>
                <Radio.Group
                  disabled={!!merge}
                  value={targetMode}
                  onChange={e => setTargetMode(e.target.value)}
                >
                  <Radio value="new">Créer une nouvelle institution</Radio>
                  <Radio value="existing">
                    Fusionner dans une institution existante
                  </Radio>
                </Radio.Group>
              </div>
              {existing && (
                <Select
                  disabled={!!merge}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: 480, display: "block", marginTop: 6 }}
                  options={options.filter(o => !sourceIds.includes(o.value))}
                  value={existingTargetId}
                  onChange={setExistingTargetId}
                  placeholder="Institution qui reçoit les établissements (ex. une fusion précédente)"
                />
              )}
              {existing && (
                <Text type="secondary">
                  Aucune nouvelle institution n'est créée : les établissements
                  sélectionnés sont rattachés à celle-ci et archivés. Sa
                  configuration actuelle entre dans la comparaison ; ce qui
                  change est journalisé et réversible.
                </Text>
              )}
            </div>
            {!existing && (
              <div>
                <Text strong>Nom de la nouvelle institution</Text>
                <Input
                  disabled={!!merge}
                  style={{ width: 480, display: "block", marginTop: 6 }}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="ex. Malévoz"
                />
                <Text type="secondary">
                  repris à l'étape suivante, où il reste modifiable
                </Text>
              </div>
            )}
            <Space wrap>
              <div>
                <Text strong>Référence pour la configuration</Text>
                <Select
                  disabled={!!merge}
                  style={{ width: 360, display: "block", marginTop: 6 }}
                  options={options.filter(
                    o =>
                      sourceIds.includes(o.value) ||
                      (existing && o.value === existingTargetId),
                  )}
                  value={donorId}
                  onChange={setDonorId}
                  placeholder={
                    existing
                      ? "Par défaut : l'institution existante"
                      : "Un des établissements"
                  }
                />
              </div>
              <div>
                <Text strong>Libellé de la fusion (historique)</Text>
                <Input
                  disabled={!!merge}
                  style={{ width: 360, display: "block", marginTop: 6 }}
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="facultatif — par défaut le nom et le nombre d'établissements"
                />
              </div>
            </Space>
            {!merge ? (
              <Button
                type="primary"
                size="large"
                loading={busy}
                onClick={create}
                disabled={
                  sourceIds.length < (existing ? 1 : 2) ||
                  (existing && !existingTargetId) ||
                  (!existing && !newName.trim())
                }
              >
                Prévisualiser
              </Button>
            ) : (
              <Space>
                <Button onClick={() => setStep(1)}>Continuer →</Button>
                {merge.status === "previewed" && (
                  <Button danger onClick={discard} loading={busy}>
                    Abandonner cette fusion
                  </Button>
                )}
                <Text type="secondary">
                  Pour changer les établissements, abandonnez et recommencez.
                </Text>
              </Space>
            )}
          </Space>
        </Card>
      )}

      {merge && step === 1 && (
        <Card
          title="2. Configuration de la nouvelle institution"
          extra={
            <Button
              size="small"
              onClick={refresh}
              loading={busy}
              disabled={disabled}
            >
              Rafraîchir la prévisualisation
            </Button>
          }
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("config")}
            <StepConfig
              merge={merge}
              preview={preview}
              decisions={decisions}
              onChange={saveDecisions}
              disabled={disabled}
            />
          </Space>
        </Card>
      )}
      {merge && step === 2 && (
        <Card title="3. Comptes en double">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("users")}
            <StepUsers
              preview={preview}
              decisions={decisions}
              onChange={saveDecisions}
              disabled={disabled}
            />
          </Space>
        </Card>
      )}
      {merge && step === S.addresses && (
        <Card title="4. Adresses — avant / après et action">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("addresses")}
            <StepAddresses
              merge={merge}
              preview={preview}
              decisions={decisions}
              onChange={saveDecisions}
              disabled={disabled}
            />
          </Space>
        </Card>
      )}
      {merge && step === S.responsables && (
        <Card title="5. Responsables — avant / après et action">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("responsables")}
            <StepResponsables
              merge={merge}
              preview={preview}
              decisions={decisions}
              onChange={saveDecisions}
              disabled={disabled}
            />
          </Space>
        </Card>
      )}
      {merge && step === S.blacklists && (
        <Card title="6. Listes noires d'interprètes">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("blacklists")}
            <StepBlacklists
              preview={preview}
              decisions={decisions}
              onChange={saveDecisions}
              disabled={disabled}
            />
          </Space>
        </Card>
      )}
      {merge && step === S.preview && (
        <Card
          title="7. Enregistrements — tout ce qui va changer"
          extra={
            <Text type="secondary">
              calculée le{" "}
              {new Date(preview.generatedAt).toLocaleString("fr-CH")}
            </Text>
          }
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("preview")}
            <MergeSummary preview={preview} decisions={decisions} />
            <StepPreview
              merge={merge}
              preview={preview}
              decisions={decisions}
            />
          </Space>
        </Card>
      )}

      {merge && step === S.result && (
        <Card title="8. Avant / Après — le résultat exact de la fusion">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("result")}
            <StepResult merge={merge} preview={preview} decisions={decisions} />
          </Space>
        </Card>
      )}

      {merge && step === S.apply && (
        <Card
          title="9. Appliquer la fusion"
          extra={
            <Button
              size="small"
              onClick={refresh}
              loading={busy}
              disabled={disabled}
            >
              Rafraîchir la prévisualisation
            </Button>
          }
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {guide("apply")}
            <MergeSummary preview={preview} decisions={decisions} />
            {disabled ? (
              <Alert
                type="info"
                showIcon
                message={`Cette fusion est ${MERGE_STATUS[
                  merge.status
                ]?.label?.toLowerCase()} — voir le détail.`}
                action={
                  <Link to={routes.institutions().mergeDetail(merge._id).path}>
                    <Button size="small">Détail</Button>
                  </Link>
                }
              />
            ) : readiness.ready ? (
              <Alert
                type="success"
                showIcon
                message="Toutes les décisions sont prises. La fusion peut être appliquée."
                description="Le serveur recalcule la prévisualisation juste avant d'appliquer et refuse si les données ont changé entre-temps. Chaque modification est journalisée avant d'être faite : la fusion reste réversible en un clic jusqu'à sa finalisation."
              />
            ) : (
              <Alert
                type="error"
                showIcon
                message={`${readiness.blockers.length} point(s) bloquent l'application`}
                description={
                  <ul style={{ margin: 0 }}>
                    {readiness.blockers.map((b, i) => (
                      <li key={`${i}.${b}`}>{b}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {!disabled && readiness.ready && (
              <Space direction="vertical">
                <Paragraph>
                  Pour confirmer, saisissez le nom de l'institution{" "}
                  {existing ? "existante" : "nouvelle"} :{" "}
                  <Text code>{targetName}</Text>
                </Paragraph>
                <Input
                  style={{ maxWidth: 420 }}
                  value={confirmName}
                  onChange={e => setConfirmName(e.target.value)}
                  placeholder={targetName}
                />
                <Button
                  type="primary"
                  danger
                  size="large"
                  loading={busy}
                  disabled={confirmName.trim() !== (targetName || "").trim()}
                  onClick={() =>
                    Modal.confirm({
                      title: "Appliquer la fusion ?",
                      okText: "Appliquer",
                      okType: "danger",
                      content: `${preview.totals.moves} enregistrements vont être rattachés à « ${targetName} » et ${preview.sources.length} établissements archivés. Réversible jusqu'à la finalisation.`,
                      onOk: apply,
                    })
                  }
                >
                  Appliquer la fusion
                </Button>
              </Space>
            )}
          </Space>
        </Card>
      )}

      {merge && step > 0 && step < S.apply && (
        <div className="merge-footer">
          <Button onClick={() => setStep(step - 1)}>← Précédent</Button>
          <div className="merge-footer-status">
            {stepPending.length ? (
              <Text strong type="danger">
                Décisions restantes sur cette étape : {stepPending.length}
              </Text>
            ) : (
              <Text strong style={{ color: "#389e0d" }}>
                ✓ étape complète
              </Text>
            )}
          </div>
          <Button type="primary" onClick={() => setStep(step + 1)}>
            Suivant → Étape {step + 2} : {STEPS[step + 1].title}
          </Button>
        </div>
      )}
    </div>
  );
}
